import axios from "axios";
import firebase from "./firebase";

const getAuthHeaders = async () => {
  const user = firebase.currentUser();
  const token = user && (await user.getIdToken());
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildShareUrl = (shareToken) => `${window.location.origin}/public/file/${shareToken}`;

export const listSecureFiles = async (clientAppointmentId) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/admin/secure-files`, {
      params: { clientAppointmentId },
      headers: await getAuthHeaders(),
    });
    return {
      data: (response.data?.data || []).map((file) => ({
        ...file,
        shareUrl: buildShareUrl(file.shareToken),
      })),
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const uploadSecureFile = async ({ clientAppointmentId, file, onUploadProgress }) => {
  try {
    const formData = new FormData();
    formData.append("clientAppointmentId", clientAppointmentId);
    formData.append("file", file);

    const response = await axios.post(`${process.env.REACT_APP_API_HOST}/admin/secure-files`, formData, {
      headers: {
        ...(await getAuthHeaders()),
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });

    const uploadedFile = response.data?.data;
    return {
      data: uploadedFile
        ? {
            ...uploadedFile,
            shareUrl: buildShareUrl(uploadedFile.shareToken),
          }
        : null,
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const deleteSecureFile = async (shareToken) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_HOST}/admin/secure-files/${shareToken}`, {
      headers: await getAuthHeaders(),
    });
    return { data: true };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const revokeSecureFileShare = async (shareToken) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/admin/secure-files/${shareToken}/revoke`,
      {},
      { headers: await getAuthHeaders() }
    );
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const regenerateSecureFileShare = async (shareToken) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/admin/secure-files/${shareToken}/regenerate-link`,
      {},
      { headers: await getAuthHeaders() }
    );
    const regeneratedFile = response.data?.data;
    return {
      data: regeneratedFile
        ? {
            ...regeneratedFile,
            shareUrl: buildShareUrl(regeneratedFile.shareToken),
          }
        : null,
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

// Re-runs the automatic Secure File creation for an outbound letter whose
// link failed (SE-646). Like the other secure-file services it resolves with
// `{ data }` on success, where `data` is `{ clientAppointmentId, pmsFileId,
// secureFileToken, secureFileStatus }`, or `{ error: { message, code } }`.
export const retryOutboundLetterSecureFile = async ({ clientAppointmentId, pmsFileId }) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/admin/secure-files/outbound-letters/retry`,
      { clientAppointmentId, pmsFileId },
      { headers: await getAuthHeaders() }
    );
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const getSecureFilePreview = async (shareToken) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/admin/secure-files/${shareToken}/preview`, {
      headers: await getAuthHeaders(),
    });
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const getSecureFileAccessLogs = async (shareToken) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/admin/secure-files/${shareToken}/access-logs`, {
      headers: await getAuthHeaders(),
    });
    return { data: response.data?.data || [] };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const getPublicSecureFileInfo = async (shareToken) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/public/secure-files/${shareToken}`);
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const verifyPublicSecureFileAccess = async ({ shareToken, password }) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_HOST}/public/secure-files/${shareToken}/verify`, {
      password,
    });
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const downloadPublicSecureFile = async ({ shareToken, accessToken }) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/public/secure-files/${shareToken}/download`, {
      headers: {
        "x-access-token": accessToken,
      },
      responseType: "blob",
    });
    return { data: response.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export { buildShareUrl };
